import { useCallback, useEffect, useState } from 'react'

import { sfx } from '../../../lib/sound'
import { TouchPad, useDirectionKeys, type GameProps } from '../GameShell'

const SIZE = 4
type Grid = number[][]

function emptyGrid(): Grid {
  return Array.from({ length: SIZE }, () => Array<number>(SIZE).fill(0))
}

function spawn(grid: Grid): Grid {
  const free: [number, number][] = []
  grid.forEach((row, y) =>
    row.forEach((value, x) => {
      if (value === 0) free.push([y, x])
    }),
  )
  if (free.length === 0) return grid

  const [y, x] = free[Math.floor(Math.random() * free.length)]
  const next = grid.map((row) => [...row])
  // 2 most of the time, occasionally a 4 -- the standard distribution.
  next[y][x] = Math.random() < 0.9 ? 2 : 4
  return next
}

/** Collapse one row to the left, returning the row and the points scored. */
function collapse(row: number[]): { row: number[]; gained: number } {
  const values = row.filter((v) => v !== 0)
  const merged: number[] = []
  let gained = 0

  for (let i = 0; i < values.length; i++) {
    // Each tile may merge only once per move, hence the skip.
    if (values[i] === values[i + 1]) {
      const sum = values[i] * 2
      merged.push(sum)
      gained += sum
      i++
    } else {
      merged.push(values[i])
    }
  }

  while (merged.length < SIZE) merged.push(0)
  return { row: merged, gained }
}

function rotate(grid: Grid): Grid {
  // Clockwise. Every direction is handled by rotating to "left", collapsing,
  // then rotating back -- one movement routine instead of four.
  return grid[0].map((_, x) => grid.map((row) => row[x]).reverse())
}

function rotateTimes(grid: Grid, times: number): Grid {
  let result = grid
  for (let i = 0; i < times; i++) result = rotate(result)
  return result
}

const TURNS: Record<'left' | 'up' | 'right' | 'down', number> = {
  left: 0,
  up: 3,
  right: 2,
  down: 1,
}

function move(grid: Grid, direction: keyof typeof TURNS) {
  const turns = TURNS[direction]
  // Rotate so the move becomes "left", collapse, then rotate back.
  const rotated = rotateTimes(grid, turns)
  let gained = 0
  const collapsed = rotated.map((row) => {
    const result = collapse(row)
    gained += result.gained
    return result.row
  })
  const restored = rotateTimes(collapsed, (4 - turns) % 4)
  const changed = JSON.stringify(restored) !== JSON.stringify(grid)
  return { grid: restored, gained, changed }
}

function hasMoves(grid: Grid): boolean {
  return (['left', 'right', 'up', 'down'] as const).some(
    (d) => move(grid, d).changed,
  )
}

const TILE_STYLES: Record<number, string> = {
  0: 'bg-canvas text-transparent',
  2: 'bg-surface text-ink',
  4: 'bg-brand-soft text-ink',
  8: 'bg-brand/30 text-ink',
  16: 'bg-brand/45 text-ink',
  32: 'bg-brand/60 text-white',
  64: 'bg-brand/75 text-white',
  128: 'bg-brand text-white',
  256: 'bg-brand text-white',
  512: 'bg-positive/70 text-white',
  1024: 'bg-positive text-white',
  2048: 'bg-positive text-white',
}

export default function Game2048({ onGameOver, onScore }: GameProps) {
  const [grid, setGrid] = useState<Grid>(() => spawn(spawn(emptyGrid())))
  const [score, setScore] = useState(0)
  const [over, setOver] = useState(false)

  const shift = useCallback(
    (direction: keyof typeof TURNS) => {
      if (over) return
      setGrid((current) => {
        const result = move(current, direction)
        if (!result.changed) return current

        sfx.tick()
        if (result.gained > 0) {
          sfx.pop()
          setScore((s) => {
            const next = s + result.gained
            onScore?.(next)
            return next
          })
        }

        const withNew = spawn(result.grid)
        if (!hasMoves(withNew)) {
          setOver(true)
          // Deferred: calling onGameOver during a state updater would set state
          // on the parent mid-render.
          setTimeout(() => onGameOver(score + result.gained), 0)
        }
        return withNew
      })
    },
    [onGameOver, onScore, over, score],
  )

  useDirectionKeys(shift, !over)

  useEffect(() => {
    onScore?.(score)
  }, [score, onScore])

  return (
    <div>
      <div className="mx-auto grid max-w-sm grid-cols-4 gap-2 rounded-xl bg-border-subtle p-2">
        {grid.flatMap((row, y) =>
          row.map((value, x) => (
            <div
              key={`${y}-${x}`}
              className={`grid aspect-square place-items-center rounded-lg text-2xl
                font-semibold tabular-nums transition-colors ${
                  TILE_STYLES[value] ?? 'bg-positive text-white'
                }`}
            >
              {value || ''}
            </div>
          )),
        )}
      </div>
      <TouchPad onDirection={shift} />
    </div>
  )
}
